import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  Button,
  Zoom,
  useTheme,
  IconButton
} from '@mui/material';
import {
  ArrowRight,
  Diamond,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  Zap
} from 'lucide-react';

// Estilos de tarjeta disponibles (agregado apple_cash)
const getCardStyles = (t) => [
  {
    id: 'liquid_glass',
    name: t('agtfin.setup.cardStyleSelector.styles.liquidGlass.name'),
    description: t('agtfin.setup.cardStyleSelector.styles.liquidGlass.description'),
    icon: <Diamond size={24} />
  },
  {
    id: 'omverse',
    name: t('agtfin.setup.cardStyleSelector.styles.omverse.name'),
    description: t('agtfin.setup.cardStyleSelector.styles.omverse.description'),
    icon: <Sparkles size={24} />
  },
  {
    id: 'apple_cash',
    name: t('agtfin.setup.cardStyleSelector.styles.appleCash.name'),
    description: t('agtfin.setup.cardStyleSelector.styles.appleCash.description'),
    icon: <Zap size={24} />
  }
];

// Componente del ícono Ompay
const OmpayIcon = ({ size = 60, className = "" }) => (
  <svg 
    width={size} 
    height={size * 0.6} 
    viewBox="0 0 100 60" 
    className={className}
    fill="currentColor"
  >
    <circle cx="20" cy="30" r="15" opacity="0.8"/>
    <circle cx="50" cy="30" r="15" opacity="0.6"/>
    <circle cx="80" cy="30" r="15" opacity="0.4"/>
    <rect x="10" y="45" width="80" height="3" rx="1.5" opacity="0.3"/>
  </svg>
);

// Componente del ícono SoundWave
const SoundWaveIcon = ({ size = 20, className = "" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 20 20" 
    className={className}
    fill="currentColor"
  >
    <rect x="2" y="8" width="2" height="4" opacity="0.6"/>
    <rect x="6" y="6" width="2" height="8" opacity="0.8"/>
    <rect x="10" y="4" width="2" height="12" opacity="1"/>
    <rect x="14" y="7" width="2" height="6" opacity="0.7"/>
  </svg>
);

// Componente PixelCanvas mejorado para ejecutarse automáticamente
const PixelCanvas = ({ colors = "#f8fafc,#f1f5f9,#cbd5e1", gap = 5, speed = 35 }) => {
  const canvasRef = React.useRef(null);
  const pixelsRef = React.useRef([]);
  const animationRef = React.useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const colorArray = colors.split(',');

    class Pixel {
      constructor(canvas, context, x, y, color, speed, delay) {
        this.width = canvas.width;
        this.height = canvas.height;
        this.ctx = context;
        this.x = x;
        this.y = y;
        this.color = color;
        this.speed = this.getRandomValue(0.1, 0.9) * speed;
        this.size = 0;
        this.sizeStep = Math.random() * 0.4;
        this.minSize = 0.5;
        this.maxSizeInteger = 2;
        this.maxSize = this.getRandomValue(this.minSize, this.maxSizeInteger);
        this.delay = delay;
        this.counter = 0;
        this.counterStep = Math.random() * 4 + (this.width + this.height) * 0.01;
        this.isIdle = false;
        this.isReverse = false;
        this.isShimmer = false;
        this.cycleComplete = false;
      }

      getRandomValue(min, max) {
        return Math.random() * (max - min) + min;
      }

      draw() {
        const centerOffset = this.maxSizeInteger * 0.5 - this.size * 0.5;
        this.ctx.fillStyle = this.color;
        this.ctx.fillRect(this.x + centerOffset, this.y + centerOffset, this.size, this.size);
      }

      animate() {
        if (this.counter <= this.delay) {
          this.counter += this.counterStep;
          return;
        }

        if (this.size >= this.maxSize && !this.isShimmer) {
          this.isShimmer = true;
        }

        if (this.isShimmer) {
          this.shimmer();
        } else {
          this.size += this.sizeStep;
        }

        this.draw();
      }

      shimmer() {
        if (this.size >= this.maxSize) {
          this.isReverse = true;
        } else if (this.size <= this.minSize) {
          this.isReverse = false;
          if (!this.cycleComplete) {
            this.cycleComplete = true;
            setTimeout(() => {
              this.counter = 0;
              this.cycleComplete = false;
              this.isShimmer = false;
            }, Math.random() * 3000 + 1000);
          }
        }

        if (this.isReverse) {
          this.size -= this.speed;
        } else {
          this.size += this.speed;
        }
      }
    }

    const resizeCanvas = (width, height) => {
      const nextWidth = Math.floor(width);
      const nextHeight = Math.floor(height);

      if (!nextWidth || !nextHeight) return;
      if (canvas.width === nextWidth && canvas.height === nextHeight) return;

      canvas.width = nextWidth;
      canvas.height = nextHeight;

      pixelsRef.current = [];
      createPixels();
      startAnimation();
    };

    const getDistanceToCanvasCenter = (x, y) => {
      const dx = x - canvas.width / 2;
      const dy = y - canvas.height / 2;
      return Math.sqrt(dx * dx + dy * dy);
    };

    const createPixels = () => {
      for (let x = 0; x < canvas.width; x += gap) {
        for (let y = 0; y < canvas.height; y += gap) {
          const color = colorArray[Math.floor(Math.random() * colorArray.length)];
          const delay = getDistanceToCanvasCenter(x, y) * 0.5;
          pixelsRef.current.push(new Pixel(canvas, ctx, x, y, color, speed * 0.001, delay));
        }
      }
    };

    const animate = () => {
      animationRef.current = requestAnimationFrame(animate);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      for (let i = 0; i < pixelsRef.current.length; i++) {
        pixelsRef.current[i].animate();
      }
    };

    const startAnimation = () => {
      cancelAnimationFrame(animationRef.current);
      animate();
    };

    const rect = canvas.getBoundingClientRect();
    resizeCanvas(rect.width, rect.height);

    const observedElement = canvas.parentElement || canvas;
    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      resizeCanvas(entry.contentRect.width, entry.contentRect.height);
    });
    resizeObserver.observe(observedElement);

    return () => {
      resizeObserver.disconnect();
      cancelAnimationFrame(animationRef.current);
    };
  }, [colors, gap, speed]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none'
      }}
    />
  );
};

// Componente para el efecto Liquid Glass SIMPLIFICADO
const LiquidGlassEffect = () => {
  return (
    <>
      {/* Imagen de fondo animada */}
      <Box
        sx={{
          position: 'absolute',
          top: '-10%',
          left: '-10%',
          right: '-10%',
          bottom: '-10%',
          backgroundImage: `url('https://images.pexels.com/photos/2317711/pexels-photo-2317711.jpeg?auto=compress&cs=tinysrgb&w=800')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          filter: 'brightness(0.7) contrast(1.1) saturate(1.2)',
          animation: 'liquidMove 12s ease-in-out infinite',
          '@keyframes liquidMove': {
            '0%, 100%': { 
              transform: 'scale(1.15) translate(0, 0)',
            },
            '25%': { 
              transform: 'scale(1.2) translate(-2%, 1%)',
            },
            '50%': { 
              transform: 'scale(1.25) translate(1%, -2%)',
            },
            '75%': { 
              transform: 'scale(1.2) translate(-1%, -1%)',
            }
          },
          zIndex: 1
        }}
      />

      {/* Glass Effect - Solo distorsión sutil */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          borderRadius: 'inherit',
          zIndex: 2,
          backdropFilter: 'blur(4px) saturate(200%)',
          background: 'rgba(255, 255, 255, 0.1)',
          pointerEvents: 'none'
        }}
      />
    </>
  );
};

// Componente para el patrón iridiscente de Apple Cash con gradiente animado
const AppleCashPattern = () => {
  const canvasRef = React.useRef(null);
  const allDotsRef = React.useRef([]);
  const animationRef = React.useRef(null);
  const timeRef = React.useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resizeCanvas = (width, height) => {
      const nextWidth = Math.floor(width);
      const nextHeight = Math.floor(height);

      if (!nextWidth || !nextHeight) return;
      if (canvas.width === nextWidth && canvas.height === nextHeight) return;

      canvas.width = nextWidth;
      canvas.height = nextHeight;
    };

    const createPattern = () => {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      allDotsRef.current = [];

      const originFocalPoint = { 
        x: canvas.width * 0.5, 
        y: canvas.height * 1.4
      }; 
      
      const rows = 10;
      const baseSizeMin = 1;
      const baseSizeMax = 6;

      for (let i = 0; i < rows; i++) {
        const centerRow = Math.floor(rows / 2);
        const distanceFromCenter = Math.abs(i - centerRow);
        const maxDistance = Math.max(centerRow, rows - centerRow - 1);
        
        const normalizedDistance = distanceFromCenter / maxDistance;
        const sizeMultiplier = 1 - Math.pow(normalizedDistance, 2.5);
        const size = baseSizeMin + (baseSizeMax - baseSizeMin) * sizeMultiplier;
        
        const radius = 40 + (i * 18);
        const startAngle = Math.PI * 0.15;
        const endAngle = Math.PI * 0.85;   
        const totalAngle = endAngle - startAngle;
        
        const circumferenceSegment = radius * totalAngle;
        const idealSpacing = 16;
        const dotsInRow = Math.max(6, Math.floor(circumferenceSegment / idealSpacing));
        const angleStep = totalAngle / Math.max(1, dotsInRow - 1);
        
        for (let j = 0; j < dotsInRow; j++) {
          const angle = startAngle + (j * angleStep);
          
          const x = originFocalPoint.x + radius * Math.cos(angle);
          const y = originFocalPoint.y - radius * Math.sin(angle);
          
          if (x < 20 || x > canvas.width - 20 || y < 10 || y > canvas.height - 10) continue;
          
          const dot = {
            x, y, 
            size, 
            scale: sizeMultiplier,
            rowIndex: i,
            dotIndex: j,
            centerDistance: distanceFromCenter,
            normalizedX: (x - 20) / (canvas.width - 40),
            normalizedY: (y - 10) / (canvas.height - 20)
          };
          
          allDotsRef.current.push(dot);
        }
      }
    };

    const drawPattern = (currentTime) => {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      const colorTransitionSpeed = 30;
      const globalHue = (currentTime * colorTransitionSpeed) % 360;
      
      const globalShimmer = Math.sin(currentTime * 0.8) * 0.2 + 0.8;
      
      allDotsRef.current.forEach((dot, index) => {
        const baseSaturation = 80;
        const baseLightness = 65;
        
        const positionVariation = Math.sin(dot.normalizedX * Math.PI + dot.normalizedY * Math.PI) * 0.1;
        
        const hue = globalHue;
        const saturation = Math.min(95, baseSaturation + (globalShimmer * 15));
        const lightness = Math.max(45, Math.min(80, baseLightness + (globalShimmer * 15) + (positionVariation * 10)));
        
        const gradient = ctx.createRadialGradient(
          dot.x, dot.y, 0,
          dot.x, dot.y, dot.size * 1.5
        );
        
        const centerColor = `hsl(${hue}, ${saturation}%, ${Math.min(85, lightness + 15)}%)`;
        const edgeColor = `hsl(${(hue + 20) % 360}, ${Math.max(50, saturation - 20)}%, ${Math.max(35, lightness - 20)}%)`;
        
        gradient.addColorStop(0, centerColor);
        gradient.addColorStop(1, edgeColor);
        
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(dot.x, dot.y, dot.size, 0, Math.PI * 2);
        ctx.fill();
        
        const arrowSize = dot.size * 0.7;
        const dx = dot.x - canvas.width * 0.5;
        const dy = dot.y - canvas.height * 1.4;
        const angle = Math.atan2(dy, dx);
        
        ctx.save();
        ctx.translate(dot.x, dot.y);
        ctx.rotate(angle + Math.PI/2);
        ctx.strokeStyle = `hsl(${(hue + 180) % 360}, ${saturation * 0.7}%, ${Math.min(30, lightness * 0.5)}%)`;
        ctx.lineWidth = Math.max(1, dot.size * 0.15);
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(0, -arrowSize);
        ctx.lineTo(0, arrowSize * 0.3);
        ctx.moveTo(0, -arrowSize);
        ctx.lineTo(-arrowSize * 0.3, -arrowSize * 0.7);
        ctx.moveTo(0, -arrowSize);
        ctx.lineTo(arrowSize * 0.3, -arrowSize * 0.7);
        ctx.stroke();
        ctx.restore();
      });
    };

    const animate = (currentTime) => {
      const newTime = currentTime * 0.001;
      timeRef.current = newTime;
      drawPattern(newTime);
      animationRef.current = requestAnimationFrame(animate);
    };

    const rect = canvas.getBoundingClientRect();
    resizeCanvas(rect.width, rect.height);
    createPattern();
    animationRef.current = requestAnimationFrame(animate);

    const observedElement = canvas.parentElement || canvas;
    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      resizeCanvas(entry.contentRect.width, entry.contentRect.height);
      createPattern();
    });
    resizeObserver.observe(observedElement);

    return () => {
      resizeObserver.disconnect();
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        opacity: 0.9
      }}
    />
  );
};

// Función para obtener el estilo de la tarjeta SIN SOMBRAS
export const getCardStyle = (styleId) => {
  if (styleId === 'liquid_glass') {
    return {
      position: 'relative',
      overflow: 'hidden',
      background: 'transparent',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 2.2)',
    };
  } else if (styleId === 'omverse') {
    return {
      background: '#000000',
      backgroundImage: 'radial-gradient(white, rgba(255,255,255,0.2) 2px, transparent 40px), radial-gradient(white, rgba(255,255,255,0.15) 1px, transparent 30px)',
      backgroundSize: '550px 550px, 350px 350px',
      backgroundPosition: '0 0, 40px 60px',
      border: '1px solid rgba(255,255,255,0.2)'
    };
  } else if (styleId === 'apple_cash') {
    return {
      background: '#000000',
      backgroundImage: `
        repeating-linear-gradient(45deg, transparent, transparent 0.5px, rgba(255,255,255,0.015) 0.5px, rgba(255,255,255,0.015) 1px),
        repeating-linear-gradient(-45deg, transparent, transparent 0.5px, rgba(255,255,255,0.01) 0.5px, rgba(255,255,255,0.01) 1px)
      `,
      border: '1px solid rgba(255,255,255,0.2)',
      position: 'relative',
      overflow: 'hidden'
    };
  }
  return {};
};

// Componente de tarjeta de crédito CORREGIDO
const CreditCardPreview = ({ style, name = "Name" }) => {
  const { t } = useTranslation();
  const cardStyle = getCardStyle(style);
  // const selectedStyle = cardStyles.find(s => s.id === style); // Unused variable

  return (
    <Card
      sx={{
        width: '100%',
        height: 200,
        borderRadius: 4,
        p: 3,
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        ...cardStyle
      }}
    >
      {/* Efecto pixel canvas para Omverse */}
      {style === 'omverse' && (
        <PixelCanvas 
          colors="#f8fafc,#f1f5f9,#cbd5e1" 
          gap={5} 
          speed={35} 
        />
      )}

      {/* Efecto liquid glass CORREGIDO */}
      {style === 'liquid_glass' && <LiquidGlassEffect />}

      {/* Patrón iridiscente para Apple Cash */}
      {style === 'apple_cash' && <AppleCashPattern />}

      {/* Contenido de la tarjeta - LAYOUT CORREGIDO */}
      <Box 
        sx={{ 
          position: 'relative', 
          zIndex: 5,
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column' 
        }}
      >
        
        {/* Para Apple Cash: Header agrupado arriba sin espaciado */}
        {style === 'apple_cash' ? (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0 }}>
            <Box>
              <Typography variant="h6" sx={{ 
                fontFamily: 'monospace',
                letterSpacing: 1,
                fontSize: '1rem',
                mb: 0.3,
                lineHeight: 1,
                textShadow: '0 0 10px rgba(0,0,0,0.8)'
              }}>
                {name || t('agtfin.setup.cardStyleSelector.name')}
              </Typography>
              <Typography variant="caption" sx={{
                opacity: 0.8,
                fontSize: '0.75rem',
                lineHeight: 1,
                textShadow: '0 0 8px rgba(0,0,0,0.6)'
              }}>
                {t('agtfin.setup.cardStyleSelector.digitalFinance')}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{
              opacity: 0.8,
              fontSize: '0.875rem',
              textShadow: '0 0 8px rgba(0,0,0,0.6)'
            }}>
              {t('agtfin.setup.cardStyleSelector.styles.appleCash.name')}
            </Typography>
          </Box>
        ) : (
          /* Para Omverse y Liquid Glass: Layout estándar */
          <>
            {/* Header con logo y tipo */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <OmpayIcon size={60} className="text-gray-300" />
              <Typography variant="body2" sx={{
                opacity: 0.8,
                textShadow: style === 'liquid_glass' ? '0 2px 4px rgba(0,0,0,0.5)' : 'none'
              }}>
                {style === 'omverse' ? t('agtfin.setup.cardStyleSelector.styles.omverse.name') : t('agtfin.setup.cardStyleSelector.styles.liquidGlass.name')}
              </Typography>
            </Box>
            
            {/* Spacer para empujar contenido hacia abajo */}
            <Box sx={{ flexGrow: 1 }} />
            
            {/* Información en cuadrante inferior izquierdo */}
            <Box sx={{ mb: 1 }}>
              <Typography variant="h6" sx={{ 
                fontFamily: 'monospace',
                letterSpacing: 1,
                fontSize: '1rem',
                mb: 0.5,
                textShadow: style === 'liquid_glass' ? '0 2px 4px rgba(0,0,0,0.5)' : 'none',
                fontWeight: 600
              }}>
                {name || t('agtfin.setup.cardStyleSelector.name')}
              </Typography>
              <Typography variant="caption" sx={{
                opacity: 0.8,
                textShadow: style === 'liquid_glass' ? '0 2px 4px rgba(0,0,0,0.5)' : 'none'
              }}>
                {t('agtfin.setup.cardStyleSelector.digitalFinance')}
              </Typography>
            </Box>
            
            {/* Líneas de sonido en cuadrante inferior derecho */}
            <Box sx={{ 
              position: 'absolute',
              bottom: 16,
              right: 16,
              display: 'flex', 
              gap: 0.2
            }}>
              <SoundWaveIcon size={16} className="text-white" />
              <SoundWaveIcon size={16} className="text-white" />
            </Box>
          </>
        )}
      </Box>
    </Card>
  );
};

const CardStyleSelector = ({ onStyleSelected, selectedStyle, onContinue }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [currentStyleIndex, setCurrentStyleIndex] = useState(0);

  const cardStyles = getCardStyles(t);

  // Inicializar con el primer estilo si no hay selección
  useEffect(() => {
    if (!selectedStyle && cardStyles.length > 0) {
      onStyleSelected(cardStyles[0].id);
    }
  }, [selectedStyle, onStyleSelected, cardStyles]);

  const currentStyle = cardStyles[currentStyleIndex];

  const handleStyleChange = (direction) => {
    const newIndex = direction === 'next' 
      ? (currentStyleIndex + 1) % cardStyles.length
      : (currentStyleIndex - 1 + cardStyles.length) % cardStyles.length;
    
    setCurrentStyleIndex(newIndex);
    const newStyle = cardStyles[newIndex];
    onStyleSelected(newStyle.id);
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Preview de la tarjeta */}
      <Box sx={{ mb: 4, position: 'relative' }}>
        <Zoom in timeout={600}>
          <Box sx={{ maxWidth: 350, mx: 'auto' }}>
            <CreditCardPreview
              style={currentStyle?.id}
            />
          </Box>
        </Zoom>

        {/* Navegación de estilos con chevrons circulares - Desktop */}
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: 0,
          right: 0,
          transform: 'translateY(-50%)',
          display: { xs: 'none', md: 'flex' },
          justifyContent: 'space-between',
          px: 2,
          zIndex: 10
        }}>
          <IconButton
            onClick={() => handleStyleChange('prev')}
            sx={{
              width: 40,
              height: 40,
              bgcolor: theme.palette.actualMode === 'wallpaper'
                ? 'rgba(255, 255, 255, 0.25)'
                : (theme.palette.mode === 'dark' ? 'white' : 'black'),
              color: theme.palette.actualMode === 'wallpaper'
                ? 'white'
                : (theme.palette.mode === 'dark' ? 'black' : 'white'),
              border: theme.palette.actualMode === 'wallpaper'
                ? '1px solid rgba(255, 255, 255, 0.3)'
                : `2px solid ${theme.palette.mode === 'dark' ? 'black' : 'white'}`,
              backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
              WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
              boxShadow: 'none !important',
              '&:hover': {
                bgcolor: theme.palette.actualMode === 'wallpaper'
                  ? 'rgba(255, 255, 255, 0.35)'
                  : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)'),
                transform: 'scale(1.05)',
                boxShadow: 'none !important'
              }
            }}
          >
            <ChevronLeft size={20} />
          </IconButton>
          <IconButton
            onClick={() => handleStyleChange('next')}
            sx={{
              width: 40,
              height: 40,
              bgcolor: theme.palette.actualMode === 'wallpaper'
                ? 'rgba(255, 255, 255, 0.25)'
                : (theme.palette.mode === 'dark' ? 'white' : 'black'),
              color: theme.palette.actualMode === 'wallpaper'
                ? 'white'
                : (theme.palette.mode === 'dark' ? 'black' : 'white'),
              border: theme.palette.actualMode === 'wallpaper'
                ? '1px solid rgba(255, 255, 255, 0.3)'
                : `2px solid ${theme.palette.mode === 'dark' ? 'black' : 'white'}`,
              backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
              WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
              boxShadow: 'none !important',
              '&:hover': {
                bgcolor: theme.palette.actualMode === 'wallpaper'
                  ? 'rgba(255, 255, 255, 0.35)'
                  : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)'),
                transform: 'scale(1.05)',
                boxShadow: 'none !important'
              }
            }}
          >
            <ChevronRight size={20} />
          </IconButton>
        </Box>

        {/* Navegación de estilos con chevrons circulares - Móvil */}
        <Box sx={{
          display: { xs: 'flex', md: 'none' },
          justifyContent: 'center',
          gap: 2,
          mt: 2,
          zIndex: 10
        }}>
          <IconButton
            onClick={() => handleStyleChange('prev')}
            sx={{
              width: 40,
              height: 40,
              bgcolor: theme.palette.actualMode === 'wallpaper'
                ? 'rgba(255, 255, 255, 0.25)'
                : (theme.palette.mode === 'dark' ? 'white' : 'black'),
              color: theme.palette.actualMode === 'wallpaper'
                ? 'white'
                : (theme.palette.mode === 'dark' ? 'black' : 'white'),
              border: theme.palette.actualMode === 'wallpaper'
                ? '1px solid rgba(255, 255, 255, 0.3)'
                : `2px solid ${theme.palette.mode === 'dark' ? 'black' : 'white'}`,
              backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
              WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
              boxShadow: 'none !important',
              '&:hover': {
                bgcolor: theme.palette.actualMode === 'wallpaper'
                  ? 'rgba(255, 255, 255, 0.35)'
                  : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)'),
                transform: 'scale(1.05)',
                boxShadow: 'none !important'
              }
            }}
          >
            <ChevronLeft size={20} />
          </IconButton>
          <IconButton
            onClick={() => handleStyleChange('next')}
            sx={{
              width: 40,
              height: 40,
              bgcolor: theme.palette.actualMode === 'wallpaper'
                ? 'rgba(255, 255, 255, 0.25)'
                : (theme.palette.mode === 'dark' ? 'white' : 'black'),
              color: theme.palette.actualMode === 'wallpaper'
                ? 'white'
                : (theme.palette.mode === 'dark' ? 'black' : 'white'),
              border: theme.palette.actualMode === 'wallpaper'
                ? '1px solid rgba(255, 255, 255, 0.3)'
                : `2px solid ${theme.palette.mode === 'dark' ? 'black' : 'white'}`,
              backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
              WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
              boxShadow: 'none !important',
              '&:hover': {
                bgcolor: theme.palette.actualMode === 'wallpaper'
                  ? 'rgba(255, 255, 255, 0.35)'
                  : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)'),
                transform: 'scale(1.05)',
                boxShadow: 'none !important'
              }
            }}
          >
            <ChevronRight size={20} />
          </IconButton>
        </Box>
      </Box>

      {/* Información del estilo actual */}
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{
          fontWeight: 600,
          mb: 1,
          color: theme.palette.actualMode === 'wallpaper'
            ? 'white'
            : (theme.palette.mode === 'dark' ? 'white' : 'black')
        }}>
          {currentStyle?.name}
        </Typography>
        <Typography variant="body2" sx={{
          opacity: 0.7,
          color: theme.palette.actualMode === 'wallpaper'
            ? 'white'
            : (theme.palette.mode === 'dark' ? 'white' : 'black')
        }}>
          {currentStyle?.description}
        </Typography>
      </Box>

      {/* Indicadores de página */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 4 }}>
        {cardStyles.map((_, index) => (
          <Box
            key={index}
            sx={{
              width: currentStyleIndex === index ? 24 : 8,
              height: 8,
              borderRadius: 4,
              bgcolor: currentStyleIndex === index
                ? (theme.palette.actualMode === 'wallpaper'
                  ? 'white'
                  : (theme.palette.mode === 'dark' ? 'white' : 'black'))
                : (theme.palette.actualMode === 'wallpaper'
                  ? 'rgba(255,255,255,0.4)'
                  : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)')),
              transition: 'all 0.3s ease-in-out',
              cursor: 'pointer'
            }}
            onClick={() => {
              setCurrentStyleIndex(index);
              onStyleSelected(cardStyles[index].id);
            }}
          />
        ))}
      </Box>

      {/* Botón continuar */}
      <Button
        variant="contained"
        fullWidth
        onClick={onContinue}
        endIcon={<ArrowRight size={20} />}
        sx={{
          borderRadius: '25px',
          height: '50px',
          fontSize: '1.1rem',
          fontWeight: 600,
          bgcolor: theme.palette.actualMode === 'wallpaper'
            ? 'white'
            : (theme.palette.mode === 'dark' ? 'white' : 'black'),
          color: theme.palette.actualMode === 'wallpaper'
            ? 'black'
            : (theme.palette.mode === 'dark' ? 'black' : 'white'),
          border: theme.palette.actualMode === 'wallpaper'
            ? 'none'
            : `2px solid ${theme.palette.mode === 'dark' ? 'black' : 'white'}`,
          textTransform: 'none',
          boxShadow: 'none !important',
          '&:hover': {
            bgcolor: theme.palette.actualMode === 'wallpaper'
              ? 'rgba(255, 255, 255, 0.9)'
              : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)'),
            boxShadow: 'none !important',
            transform: 'scale(1.02)'
          },
        }}
      >
        {t('agtfin.setup.cardStyleSelector.continueButton')}
      </Button>
    </Box>
  );
};

export default CardStyleSelector;
